import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import RequestService from "../../services/RequestService";
import RelanceDevisService from "../../services/RelanceDevisService";
import { ARCHIVED_REASONS_LABELS } from "../../utils/ArchivedReasonsUtils";
import Select from "../../components/Atomes/Select/Select";

const MAX_CUSTOM_REASON_LENGTH = 200;
const RELANCE_DEVIS_PREFIX = "/relance-devis/";

const getActionAndIdFromPathname = (pathname) => {
  if (!pathname.startsWith(RELANCE_DEVIS_PREFIX))
    return { action: null, id: null, token: null };
  const segments = pathname.slice(RELANCE_DEVIS_PREFIX.length).split("/");
  const action = segments[0] || null;
  const id = segments[1] || null;
  const token = segments[2] || null;
  if (!["archive", "rappeler", "cloture"].includes(action) || !id || !token)
    return { action: null, id: null, token: null };
  return { action, id, token };
};

const RelanceDevis = () => {
  const { pathname } = useLocation();
  const { action, id, token } = getActionAndIdFromPathname(pathname);

  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveDone, setArchiveDone] = useState(false);
  const [archiveError, setArchiveError] = useState(false);

  const [rappelerLoading, setRappelerLoading] = useState(false);
  const [rappelerDone, setRappelerDone] = useState(false);

  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [clotureSubmitting, setClotureSubmitting] = useState(false);
  const [clotureDone, setClotureDone] = useState(false);

  const reasonCodes = Object.keys(ARCHIVED_REASONS_LABELS);
  const reasonLabels = Object.values(ARCHIVED_REASONS_LABELS);
  const selectOptions = ["Sélectionnez une raison", ...reasonLabels];
  const selectValues = ["", ...reasonCodes];
  const isOtherSelected = selectedReason === "other";
  const customReasonValid =
    customReason.trim().length > 0 &&
    customReason.length <= MAX_CUSTOM_REASON_LENGTH;
  const canSubmitCloture =
    selectedReason !== "" && (selectedReason !== "other" || customReasonValid);

  // Cas 1 – Archive "Projet en pause"
  useEffect(() => {
    if (
      action !== "archive" ||
      !id ||
      archiveLoading ||
      archiveDone ||
      archiveError
    )
      return;
    setArchiveLoading(true);
    RelanceDevisService.archiveWithProjetEnPause(id, token)
      .then((request) => {
        if (request?.status === 200) {
          setArchiveDone(true);
          toast.success("Votre projet a été mis en pause.");
          RelanceDevisService.redirectAfterDelay();
        } else {
          setArchiveError(true);
          toast.error("Une erreur est survenue.");
        }
      })
      .catch(() => {
        setArchiveError(true);
        toast.error("Une erreur est survenue.");
      })
      .finally(() => {
        setArchiveLoading(false);
      });
  }, [action, id, token, archiveDone, archiveError, archiveLoading]);

  // Cas 2 – Rappeler le commercial
  const handleRappeler = async () => {
    if (!id || rappelerLoading || rappelerDone) return;
    setRappelerLoading(true);
    try {
      const request = await RelanceDevisService.requestCallbackCommercial(id, token);
      if (request?.status === 200) {
        setRappelerDone(true);
        toast.success(
          "Votre demande a bien été enregistrée. Un commercial vous rappellera.",
        );
        RelanceDevisService.redirectAfterDelay();
      } else {
        toast.error("Une erreur est survenue.");
      }
    } catch {
      toast.error("Une erreur est survenue.");
    } finally {
      setRappelerLoading(false);
    }
  };

  // Cas 3 – Clôture : formulaire de choix
  const handleClotureSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmitCloture || !id || !token || clotureSubmitting) return;
    const reason =
      selectedReason === "other" ? customReason.trim() : selectedReason;
    setClotureSubmitting(true);
    try {
      const request = await RelanceDevisService.archiveWithReason(id, reason, token);
      if (request?.status === 200) {
        setClotureDone(true);
        toast.success("Votre choix a bien été enregistré.");
      } else {
        toast.error("Une erreur est survenue.");
      }
    } catch {
      toast.error("Une erreur est survenue.");
    } finally {
      setClotureSubmitting(false);
    }
  };

  if (!action || !id || !token) {
    return (
      <div
        className="relance-devis"
        style={{ padding: "2rem", textAlign: "center" }}
      >
        <p>Lien invalide.</p>
      </div>
    );
  }

  // Cas 1 – Archive
  if (action === "archive") {
    return (
      <div className="relance-devis page-connection">
        <aside>
          <img src="/images/pictos/logo-mbe-simple.svg" />
        </aside>
        <div>
          {archiveLoading && <p className="text-primary ">Enregistrement en cours…</p>}
          {archiveDone && (
            <p className="text-primary">Votre projet a été mis en pause. Vous allez être redirigé.</p>
          )}
          {archiveError && (
            <p className="text-primary">Une erreur est survenue. Veuillez réessayer plus tard.</p>
          )}
          {!archiveLoading && !archiveDone && !archiveError && (
            <p className="text-primary">Enregistrement en cours…</p>
          )}
        </div>
      </div>
    );
  }

  // Cas 2 – Rappeler
  if (action === "rappeler") {
    return (
      <div className="relance-devis page-connection">
        <aside>
          <img src="/images/pictos/logo-mbe-simple.svg" />
        </aside>
        <div>
          <h2 style={{ marginBottom: "1rem" }}>
            Souhaitez-vous être rappelé ?
          </h2>
          {rappelerDone ? (
            <p className="text-primary">
              Votre demande a bien été enregistrée. Vous allez être redirigé.
            </p>
          ) : (
            <button
              type="button"
              className="btn-default btn-default-primary"
              onClick={handleRappeler}
              disabled={rappelerLoading}
            >
              {rappelerLoading ? "Enregistrement…" : "Oui, rappelez-moi"}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Cas 3 – Clôture : formulaire
  if (action === "cloture") {
    return (
      <div className="relance-devis page-connection">
        <aside>
          <img src="/images/pictos/logo-mbe-simple.svg" />
        </aside>
        <div>
          <h2 style={{ marginBottom: "1rem" }}>Formulaire de clôture de votre projet</h2>
          {clotureDone ? (
            <p className="text-primary">Votre choix a bien été enregistré.</p>
          ) : (
            <form
              onSubmit={handleClotureSubmit}
              style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                }}
              >
                <label htmlFor="relance-devis-reason" className="text-primary">
                  Raison (obligatoire)
                </label>
                <Select
                  styleSelect="select-custom-secondary"
                  data={selectOptions}
                  value={selectValues}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  selectedValue={selectedReason}
                  showDefaultOption={false}
                />
              </div>
              {isOtherSelected && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  <label htmlFor="relance-devis-custom-reason" className="text-primary">
                    Précisez la raison (max. {MAX_CUSTOM_REASON_LENGTH}{" "}
                    caractères)
                  </label>
                  <input
                    id="relance-devis-custom-reason"
                    type="text"
                    className="form-control"
                    placeholder="Saisissez la raison"
                    value={customReason}
                    onChange={(e) =>
                      setCustomReason(
                        e.target.value.slice(0, MAX_CUSTOM_REASON_LENGTH),
                      )
                    }
                    maxLength={MAX_CUSTOM_REASON_LENGTH}
                  />
                  <p className="text-secondary" style={{ color: "var(--color-secondary, #6c757d)" }}>
                    {customReason.length} / {MAX_CUSTOM_REASON_LENGTH}{" "}
                    caractères
                  </p>
                </div>
              )}
              <button
                type="submit"
                className="btn-default btn-default-primary"
                disabled={!canSubmitCloture || clotureSubmitting}
              >
                {clotureSubmitting ? "Envoi…" : "Valider"}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default RelanceDevis;
